// 通用套餐配置（Combo Engine）V1 — 2026-08-27 上線（後端 migration 0072 + functions/modules/public-admin-package-configs）
// 場景兼容：禮盒（必含組件）/ 餐廳套餐（自選組任選 N）/ 月盒（混合）。
// 價格慣例：後端存 cents；此頁輸入/顯示 HKD。
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Plus, Edit2, Save, X, ToggleLeft, ToggleRight, Trash2, Image as ImageIcon } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import MediaPicker from '../components/MediaPicker';
import { API_BASE } from '../lib/api';

interface FixedItem { productId: number; quantity: number }
interface ChoiceGroup { groupName: string; chooseCount: number; options: number[] }
interface PackageConfig {
  id: number;
  key: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  priceCents: number;
  isActive: number;
  sortOrder: number;
  fixedItems: { productId: number; productName: string | null; quantity: number }[];
  choiceGroups: { groupName: string; chooseCount: number; options: { productId: number; productName: string | null }[] }[];
}
interface ProductOpt { id: number; name: string; is_active?: number }

const emptyForm = {
  key: '', name: '', description: '', imageUrl: '', priceHkd: '', sortOrder: '0', isActive: true,
  fixed: [] as FixedItem[],
  groups: [] as ChoiceGroup[],
};

const AdminPackageConfigs: React.FC = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<PackageConfig[]>([]);
  const [products, setProducts] = useState<ProductOpt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchAll();
  }, [navigate]);

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(''), 2500); return () => clearTimeout(t); }
  }, [success]);

  const authHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cfgRes, prodRes] = await Promise.all([
        fetch(`${API_BASE}/api/public/admin/package-configs`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/public/admin/products`, { headers: authHeaders() }),
      ]);
      if (cfgRes.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin'); return; }
      const cfgData = await cfgRes.json();
      const prodData = await prodRes.json();
      const rawConfigs = Array.isArray(cfgData) ? cfgData : cfgData.data || [];
      const rawProducts = Array.isArray(prodData) ? prodData : prodData.data || [];
      setConfigs(rawConfigs);
      setProducts(rawProducts.filter((p: ProductOpt) => p.is_active === undefined || p.is_active === 1 || p.is_active === 0));
    } catch (e: any) {
      setError(e.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setForm({ ...emptyForm });
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (c: PackageConfig) => {
    setForm({
      key: c.key, name: c.name, description: c.description || '', imageUrl: c.imageUrl || '',
      priceHkd: (c.priceCents / 100).toString(), sortOrder: String(c.sortOrder || 0), isActive: !!c.isActive,
      fixed: c.fixedItems.map((f) => ({ productId: f.productId, quantity: f.quantity })),
      groups: c.choiceGroups.map((g) => ({ groupName: g.groupName, chooseCount: g.chooseCount, options: g.options.map((o) => o.productId) })),
    });
    setEditingId(c.id);
    setShowForm(true);
  };

  const buildItems = () => [
    ...form.fixed.map((f) => ({ productId: f.productId, quantity: f.quantity })),
    ...form.groups.flatMap((g) => g.options.map((pid) => ({ productId: pid, quantity: 1, groupName: g.groupName, chooseCount: g.chooseCount }))),
  ];

  const save = async () => {
    if (!form.name.trim() || (!editingId && !form.key.trim())) { setError('請填寫 Key 與名稱'); return; }
    const priceCents = Math.round((parseFloat(form.priceHkd) || 0) * 100);
    if (priceCents <= 0) { setError('價格必須大於 0'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        key: form.key.trim().toLowerCase(), name: form.name.trim(), description: form.description.trim(),
        imageUrl: form.imageUrl.trim() || null, priceCents, sortOrder: Number(form.sortOrder || 0),
        isActive: form.isActive, items: buildItems(),
      };
      const res = await fetch(
        editingId ? `${API_BASE}/api/public/admin/package-configs/${editingId}` : `${API_BASE}/api/public/admin/package-configs`,
        {
          method: editingId ? 'PUT' : 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '儲存失敗');
      setShowForm(false);
      setSuccess(editingId ? '套餐已更新' : '套餐已建立');
      await fetchAll();
    } catch (e: any) {
      setError(e.message || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c: PackageConfig) => {
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/package-configs/${c.id}/toggle`, { method: 'POST', headers: authHeaders() });
      if (!res.ok) throw new Error('切換失敗');
      await fetchAll();
    } catch (e: any) { setError(e.message); }
  };

  const remove = async (c: PackageConfig) => {
    if (!confirm(`確定刪除套餐「${c.name}」？此操作無法還原。`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/package-configs/${c.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('刪除失敗');
      setSuccess('已刪除');
      await fetchAll();
    } catch (e: any) { setError(e.message); }
  };

  const productOptions = (selected: number, otherSelected: number[] = []) =>
    products.filter((p) => p.id === selected || !otherSelected.includes(p.id));

  const summary = (c: PackageConfig) => {
    const parts: string[] = [];
    if (c.fixedItems.length) parts.push(c.fixedItems.map((f) => `${f.productName || `#${f.productId}`}×${f.quantity}`).join('＋'));
    c.choiceGroups.forEach((g) => parts.push(`${g.groupName}（任選 ${g.chooseCount}：${g.options.map((o) => o.productName || `#${o.productId}`).join('/')}）`));
    return parts.join('＋') || '（未設組件）';
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm';
  const btn = 'px-4 py-2 rounded-lg font-medium transition';

  return (
    <AdminLayout currentPage="package-configs">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Package className="w-6 h-6 text-red-600" /> 套餐配置
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              把現有產品組合成套餐（禮盒／任選組合／餐廳套餐）以套餐價售賣
            </p>
          </div>
          <button onClick={openCreate} className={`${btn} bg-red-600 text-white hover:bg-red-700 flex items-center gap-2`}>
            <Plus size={18} /> 新增套餐
          </button>
        </div>

        {success && <div className="bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 p-3 rounded-lg text-sm">{success}</div>}
        {error && !showForm && <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-3 rounded-lg text-sm">{error}</div>}

        {loading ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">載入中…</div>
        ) : configs.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-10 text-center text-gray-500 dark:text-gray-400">
            還沒有套餐。點「新增套餐」把產品組合成麥當勞式 combo 售賣。
          </div>
        ) : (
          <div className="space-y-3">
            {configs.map((c) => (
              <div key={c.id} className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 flex items-center gap-4">
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                  {c.imageUrl ? <img src={c.imageUrl} alt={c.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-400"><Package size={22} /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-bold ${c.isActive ? 'text-gray-800 dark:text-white' : 'text-gray-400 dark:text-gray-500 line-through'}`}>{c.name}</span>
                    <span className="text-xs font-mono text-gray-400">{c.key}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${c.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                      {c.isActive ? '上架' : '下架'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">{summary(c)}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold text-red-600">HK${(c.priceCents / 100).toFixed(0)}</p>
                  <p className="text-xs text-gray-400">排序 {c.sortOrder}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => toggleActive(c)} className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400" title={c.isActive ? '下架' : '上架'}>
                    {c.isActive ? <ToggleRight size={22} className="text-green-600" /> : <ToggleLeft size={22} />}
                  </button>
                  <button onClick={() => openEdit(c)} className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400"><Edit2 size={18} /></button>
                  <button onClick={() => remove(c)} className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400"><Trash2 size={18} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">{editingId ? '編輯套餐' : '新增套餐'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X size={22} /></button>
            </div>

            {error && <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-3 rounded-lg text-sm">{error}</div>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">類型鍵 *</label>
                <input value={form.key} disabled={!!editingId} onChange={(e) => setForm({ ...form, key: e.target.value })}
                  placeholder="例如：family-set" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">套餐名稱 *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="例如：四人套餐" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">套餐價（HKD）*</label>
                <input type="number" min="0" step="0.1" value={form.priceHkd} onChange={(e) => setForm({ ...form, priceHkd: e.target.value })} placeholder="388" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序（細至大）</label>
                <input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} className={inputCls} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">描述</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="套餐說明（前台顯示）" className={inputCls} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">套餐圖片</label>
              <div className="flex gap-2">
                <input value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} placeholder="/media/..." className={inputCls} />
                <button type="button" onClick={() => setShowMediaPicker(true)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center gap-1 text-sm flex-shrink-0">
                  <ImageIcon size={16} /> 媒體庫
                </button>
              </div>
              {form.imageUrl && <img src={form.imageUrl} alt="preview" className="mt-2 w-20 h-20 rounded-lg object-cover" />}
            </div>

            {/* 必含組件 */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">必含組件（每份套餐固定包含）</label>
                <button type="button" onClick={() => setForm({ ...form, fixed: [...form.fixed, { productId: products[0]?.id || 0, quantity: 1 }] })}
                  className="text-sm text-red-600 hover:underline flex items-center gap-1"><Plus size={14} /> 加組件</button>
              </div>
              <div className="space-y-2">
                {form.fixed.map((f, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <select value={f.productId} onChange={(e) => { const fixed = [...form.fixed]; fixed[i] = { ...f, productId: Number(e.target.value) }; setForm({ ...form, fixed }); }} className={inputCls}>
                      {productOptions(f.productId, form.fixed.map((x) => x.productId)).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <input type="number" min="1" value={f.quantity} onChange={(e) => { const fixed = [...form.fixed]; fixed[i] = { ...f, quantity: Math.max(1, Number(e.target.value) || 1) }; setForm({ ...form, fixed }); }}
                      className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm" title="數量" />
                    <button type="button" onClick={() => setForm({ ...form, fixed: form.fixed.filter((_, j) => j !== i) })} className="p-2 text-gray-400 hover:text-red-600"><Trash2 size={16} /></button>
                  </div>
                ))}
                {form.fixed.length === 0 && <p className="text-xs text-gray-400">（可留空——套餐可純由自選組構成）</p>}
              </div>
            </div>

            {/* 自選組 */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">自選組（客人從中任選 N 項）</label>
                <button type="button" onClick={() => setForm({ ...form, groups: [...form.groups, { groupName: '', chooseCount: 1, options: [] }] })}
                  className="text-sm text-red-600 hover:underline flex items-center gap-1"><Plus size={14} /> 加自選組</button>
              </div>
              <div className="space-y-3">
                {form.groups.map((g, gi) => (
                  <div key={gi} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2">
                    <div className="flex gap-2 items-center">
                      <input value={g.groupName} onChange={(e) => { const groups = [...form.groups]; groups[gi] = { ...g, groupName: e.target.value }; setForm({ ...form, groups }); }}
                        placeholder="組名（例如：任選辣粉）" className={inputCls} />
                      <input type="number" min="1" value={g.chooseCount} onChange={(e) => { const groups = [...form.groups]; groups[gi] = { ...g, chooseCount: Math.max(1, Number(e.target.value) || 1) }; setForm({ ...form, groups }); }}
                        className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm" title="需選數量" />
                      <span className="text-xs text-gray-400 flex-shrink-0">需選 N</span>
                      <button type="button" onClick={() => setForm({ ...form, groups: form.groups.filter((_, j) => j !== gi) })} className="p-2 text-gray-400 hover:text-red-600"><Trash2 size={16} /></button>
                    </div>
                    {g.options.map((pid, oi) => (
                      <div key={oi} className="flex gap-2 items-center pl-4">
                        <select value={pid} onChange={(e) => { const groups = [...form.groups]; const opts = [...g.options]; opts[oi] = Number(e.target.value); groups[gi] = { ...g, options: opts }; setForm({ ...form, groups }); }} className={inputCls}>
                          {productOptions(pid, g.options).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                        <button type="button" onClick={() => { const groups = [...form.groups]; groups[gi] = { ...g, options: g.options.filter((_, j) => j !== oi) }; setForm({ ...form, groups }); }}
                          className="p-2 text-gray-400 hover:text-red-600"><Trash2 size={16} /></button>
                      </div>
                    ))}
                    <button type="button" onClick={() => { const groups = [...form.groups]; groups[gi] = { ...g, options: [...g.options, products[0]?.id || 0] }; setForm({ ...form, groups }); }}
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1 pl-4"><Plus size={14} /> 加可選產品</button>
                  </div>
                ))}
                {form.groups.length === 0 && <p className="text-xs text-gray-400">（可留空——套餐可純由必含組件構成，如禮盒）</p>}
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} className="w-4 h-4 accent-red-600" />
                上架
              </label>
              <div className="flex gap-2">
                <button onClick={() => setShowForm(false)} className={`${btn} border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300`}>取消</button>
                <button onClick={save} disabled={saving} className={`${btn} bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 flex items-center gap-2`}>
                  <Save size={16} /> {saving ? '儲存中…' : '儲存'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMediaPicker && (
        <MediaPicker
          selectedUrl={form.imageUrl}
          category="general"
          onSelect={(url) => { setForm({ ...form, imageUrl: url }); setShowMediaPicker(false); }}
          onClose={() => setShowMediaPicker(false)}
        />
      )}
    </AdminLayout>
  );
};

export default AdminPackageConfigs;
